const cleanValue = (value) => `${value || ''}`.trim()

const padDatePart = (value) => `${value}`.padStart(2, '0')

const formatDisplayName = (value) => cleanValue(value).replace(/\./g, ' ')

const getVersionDisplayName = (version, status) =>
  status === 'draft'
    ? (version?.created_by?.display_name ??
      version?.createdBy?.display_name ??
      version?.activated_by?.display_name ??
      version?.activatedBy?.display_name ??
      version?.published_by ??
      version?.publishedBy)
    : (version?.activated_by?.display_name ??
      version?.activatedBy?.display_name ??
      version?.published_by ??
      version?.publishedBy ??
      version?.created_by?.display_name ??
      version?.createdBy?.display_name)

const formatPromptModeTimestamp = (value) => {
  const rawValue = cleanValue(value)

  if (!rawValue) {
    return ''
  }

  const formattedMatch = rawValue.match(
    /^(\d{2})\/(\d{2})\/(\d{4}),?\s+(\d{2}):(\d{2}):(\d{2})$/,
  )

  if (formattedMatch) {
    const [, day, month, year, hours, minutes, seconds] = formattedMatch
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`
  }

  const parsedDate = new Date(rawValue)

  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue
  }

  return (
    [
      padDatePart(parsedDate.getDate()),
      padDatePart(parsedDate.getMonth() + 1),
      parsedDate.getFullYear(),
    ].join('/') +
    ` ${padDatePart(parsedDate.getHours())}:${padDatePart(
      parsedDate.getMinutes(),
    )}:${padDatePart(parsedDate.getSeconds())}`
  )
}

const normalizePromptModeResponse = (response) => {
  if (Array.isArray(response)) {
    return response
  }

  if (Array.isArray(response?.prompt_modes)) {
    return response.prompt_modes
  }

  if (Array.isArray(response?.promptModes)) {
    return response.promptModes
  }

  return response ? [response] : []
}

const normalizePromptModeVersionResponse = (response) => {
  if (Array.isArray(response)) {
    return response
  }

  if (Array.isArray(response?.prompt_mode_versions)) {
    return response.prompt_mode_versions
  }

  if (Array.isArray(response?.promptModeVersions)) {
    return response.promptModeVersions
  }

  if (Array.isArray(response?.versions)) {
    return response.versions
  }

  return response ? [response] : []
}

export const mapPromptModeResponse = (response = []) =>
  normalizePromptModeResponse(response)
    .map((promptMode = {}) => {
      const id = cleanValue(promptMode?.id)
      const name = cleanValue(promptMode?.name)
      return {
        id,
        code: cleanValue(promptMode?.code),
        name,
        description: cleanValue(promptMode?.description),
        dsCapability: cleanValue(promptMode?.ds_capability),
        enabled: promptMode?.enabled !== false,
      }
    })
    .filter(({ id, name }) => Boolean(id && name))

export const mapPromptModeVersionResponse = (response = []) =>
  normalizePromptModeVersionResponse(response)
    .map((version = {}, index) => {
      const status = cleanValue(version?.status).toLowerCase()
      const active =
        status === 'active' ||
        version?.active === true ||
        version?.is_active === true ||
        version?.isActive === true
      const versionNumber = cleanValue(
        version?.version_number ?? version?.versionNumber ?? version?.version,
      )

      const publishedAt = formatPromptModeTimestamp(
        version?.activated_at ??
          version?.activatedAt ??
          version?.published_at ??
          version?.publishedAt ??
          version?.published ??
          version?.created_at ??
          version?.createdAt,
      )

      return {
        active,
        id:
          cleanValue(version?.id) ||
          cleanValue(version?.prompt_version_id) ||
          cleanValue(version?.promptVersionId) ||
          versionNumber ||
          `version-${index + 1}`,
        promptVersionId:
          cleanValue(version?.prompt_version_id) ||
          cleanValue(version?.promptVersionId) ||
          cleanValue(version?.id) ||
          versionNumber ||
          `version-${index + 1}`,
        changeSummary: cleanValue(
          version?.change_summary ?? version?.changeSummary ?? version?.summary,
        ),
        instructionText: cleanValue(
          version?.instruction_text ??
            version?.instructionText ??
            version?.instruction ??
            version?.content ??
            version?.prompt ??
            version?.instructions?.content ??
            version?.instructions?.active,
        ),
        publishedBy: formatDisplayName(getVersionDisplayName(version, status)),
        published: publishedAt,
        publishedAt,
        status: status || (active ? 'active' : 'inactive'),
        version:
          status === 'draft'
            ? 'Draft'
            : versionNumber
              ? `v${versionNumber.replace(/^v/i, '')}`
              : '',
      }
    })
    .filter(({ version }) => Boolean(version))

export const toPromptModeVersionBody = ({
  changeSummary = '',
  instructionText = '',
}) => ({
  change_summary: `${changeSummary}`.trim(),
  instruction_text: `${instructionText}`.trim(),
})

export const toPromptModeBody = ({
  code = '',
  name = '',
  description = '',
  dsCapability = '',
  enabled = true,
}) => ({
  code: `${code}`.trim(),
  name: `${name}`.trim(),
  description: `${description}`.trim(),
  ds_capability: `${dsCapability}`.trim(),
  enabled,
})
